import axios from "axios";
import React from "react";

const AuthContext = React.createContext();

const auth_API_URL = "/api/auth/";

// Reset every stored session key to its guest default. Shared by
// AuthProvider.logout (user-initiated sign-out) and the global 401
// interceptor in index.js (expired/invalid-token forced sign-out), so
// the two can never disagree about what "logged out" means in storage.
function clearStoredSession() {
  localStorage.setItem("Email", "guest");
  localStorage.setItem("Domain", "guest");
  localStorage.setItem("Weight", 1.0);
  localStorage.setItem("Auth", "Guest");
  localStorage.setItem("Info", "Guest");
  localStorage.setItem("Name", "Guest");
  localStorage.removeItem("TutorialDone");
  localStorage.removeItem("PracticePassed");
  localStorage.removeItem("token");
  // Per-device "I'm not in a lab" dismissal shouldn't carry across accounts.
  localStorage.removeItem("labNoticeDismissed");
}

class AuthProvider extends React.Component {
  state = {
    Email: "guest",
    Domain: "guest",
    Weight: 1.0,
    Auth: "Guest",
    Info: "Guest",
    Name: "Guest",
    TutorialDone: false,
    PracticePassed: false,
    // Whether the expertise/affiliation profile is filled in. Defaults to
    // true so guests and pre-existing accounts are never gated; only an
    // explicit `profileComplete:false` in the session (a fresh Google
    // signup) flips it false and triggers the RouteGuard.
    ProfileComplete: true,
    // Research lab (for the competition). Empty until set. Drives the
    // "set your lab" nudge (navbar dot + dashboard banner). LabNoticeDismissed
    // is the per-device "I'm not in a lab" opt-out.
    Lab: "",
    LabNoticeDismissed: false,
  };

  componentDidMount() {
    //If state is not stored in local storage, use original state
    if (
      !localStorage.getItem("Email") &&
      !localStorage.getItem("Domain") &&
      !localStorage.getItem("Weight") &&
      !localStorage.getItem("Auth") &&
      !localStorage.getItem("Info") &&
      !localStorage.getItem("Name")
    ) {
      return;
    }

    // profileComplete + lab ride inside the stored session JSON ("Info").
    let profileComplete = true;
    let lab = "";
    try {
      const info = JSON.parse(localStorage.getItem("Info"));
      if (info && typeof info === "object") {
        if (info.profileComplete === false) profileComplete = false;
        if (typeof info.lab === "string") lab = info.lab;
      }
    } catch (e) {
      // "Info" is the literal "Guest" string for guests — not JSON. Ignore.
    }

    this.setState({
      Email: localStorage.getItem("Email"),
      Domain: localStorage.getItem("Domain"),
      Weight: localStorage.getItem("Weight"),
      Auth: localStorage.getItem("Auth"),
      Info: localStorage.getItem("Info"),
      Name: localStorage.getItem("Name"),
      TutorialDone: localStorage.getItem("TutorialDone") === "true",
      PracticePassed: localStorage.getItem("PracticePassed") === "true",
      ProfileComplete: profileComplete,
      Lab: lab,
      LabNoticeDismissed: localStorage.getItem("labNoticeDismissed") === "1",
    });
  }

  // Called by views (PracticePage, TutorialPage) to refresh local auth state
  // after the server flips a flag on the user.
  setPracticePassed = (passed) => {
    this.setState({ PracticePassed: !!passed });
    localStorage.setItem("PracticePassed", String(!!passed));
  };

  authHeader = () => {
    let info = JSON.parse(localStorage.getItem("Info"));

    if (info.accessToken) {
      return { "x-access-token": info.accessToken };
    } else {
      return {};
    }
  };

  // Persist a successful auth payload (from signin OR verify-email) into
  // state + localStorage. Both endpoints return the same shape, so they
  // share this. Returns the data so callers can chain.
  applySession = (data, email) => {
    let role = "Guest";
    if (data.roles.includes("ROLE_ADMIN")) {
      role = "Admin";
    } else if (data.roles.includes("ROLE_USER")) {
      role = "User";
    }

    if (data.accessToken) {
      const tutorialDone = !!data.tutorialCompleted;
      const practicePassed = !!data.practicePassed;
      this.setState(
        {
          Email: email,
          Domain: data.domain,
          Weight: data.weight,
          Auth: role,
          Info: JSON.stringify(data),
          Name: data.fname,
          TutorialDone: tutorialDone,
          PracticePassed: practicePassed,
          // false only for a fresh Google signup; undefined/true otherwise.
          ProfileComplete: data.profileComplete !== false,
          Lab: typeof data.lab === "string" ? data.lab : "",
        },
        () => {
          localStorage.setItem("Email", email);
          localStorage.setItem("Domain", data.domain);
          localStorage.setItem("Weight", data.weight);
          localStorage.setItem("Auth", role);
          localStorage.setItem("Info", JSON.stringify(data));
          localStorage.setItem("Name", data.fname);
          localStorage.setItem("token", data.accessToken);
          localStorage.setItem("TutorialDone", String(tutorialDone));
          localStorage.setItem("PracticePassed", String(practicePassed));
        }
      );
    }
    return data;
  };

  login = ({ email, password }) => {
    return axios
      .post(auth_API_URL + "signin", { email, password })
      .then((response) => this.applySession(response.data, email));
  };

  // Sign in with a Google ID-token credential (from the GIS button). On
  // success the server returns a full session (same shape as signin) plus
  // profileComplete / isNewUser, so the caller can route a new Google user
  // to the profile form.
  googleLogin = (credential) => {
    return axios
      .post(auth_API_URL + "google", { credential })
      .then((response) => {
        if (response.data && response.data.accessToken) {
          this.applySession(response.data, response.data.email);
        }
        return response.data;
      });
  };

  // Verify a new account with the emailed 6-digit code. On success the
  // server returns a full session (auto-login), which we persist.
  verifyEmail = ({ email, otp }) => {
    return axios
      .post(auth_API_URL + "verify-email", { email, otp })
      .then((response) => {
        if (response.data && response.data.accessToken) {
          this.applySession(response.data, email);
        }
        return response.data;
      });
  };

  // Ask the server to re-send the signup verification code.
  resendVerification = (email) => {
    return axios.post(auth_API_URL + "resend-verification", { email });
  };

  completeTutorial = () => {
    return axios
      .post("/api/users/complete-tutorial", {}, {
        headers: this.authHeader(),
      })
      .then((response) => {
        this.setState({ TutorialDone: true });
        localStorage.setItem("TutorialDone", "true");
        return response.data;
      });
  };

  // Save the expertise/affiliation profile (used by the Google-signup
  // CompleteProfile form). Patches the cached session so profileComplete
  // flips true without another round-trip.
  completeProfile = (fields) => {
    return axios
      .post("/api/users/complete-profile", fields, {
        headers: this.authHeader(),
      })
      .then((response) => {
        this.setState({ ProfileComplete: true });
        try {
          const info = JSON.parse(localStorage.getItem("Info") || "{}");
          info.profileComplete = true;
          if (fields.fname) info.fname = fields.fname;
          // Keep the cached lab in sync so a re-open of the edit form shows
          // the value the user just saved (they may not re-login for a year).
          if (fields.lab !== undefined) {
            info.lab = fields.lab;
            this.setState({ Lab: fields.lab });
          }
          localStorage.setItem("Info", JSON.stringify(info));
          if (fields.fname) {
            this.setState({ Name: fields.fname });
            localStorage.setItem("Name", fields.fname);
          }
        } catch (e) {
          // Non-fatal — the server already saved the profile.
        }
        return response.data;
      });
  };

  logout = () => {
    this.setState(
      {
        Email: "guest",
        Domain: "guest",
        Weight: 1.0,
        Auth: "Guest",
        Info: "Guest",
        Name: "Guest",
        TutorialDone: false,
        PracticePassed: false,
        ProfileComplete: true,
        Lab: "",
        LabNoticeDismissed: false,
      },
      clearStoredSession
    );
  };

  // Update the cached lab (called after the dashboard banner / edit-profile
  // save) so the "set your lab" nudge disappears everywhere immediately.
  updateLab = (lab) => {
    const value = typeof lab === "string" ? lab : "";
    this.setState({ Lab: value });
    try {
      const info = JSON.parse(localStorage.getItem("Info") || "{}");
      if (info && typeof info === "object") {
        info.lab = value;
        localStorage.setItem("Info", JSON.stringify(info));
      }
    } catch (_) {
      // Info is the literal "Guest" string — nothing to patch.
    }
  };

  // "I'm not in a lab" — stop nudging on this device.
  dismissLabNotice = () => {
    this.setState({ LabNoticeDismissed: true });
    try {
      localStorage.setItem("labNoticeDismissed", "1");
    } catch (_) {}
  };

  signup = ({
    fname,
    lname,
    assocSel,
    lab,
    edu,
    exp,
    expEEG,
    expMoBI,
    expICA,
    cleanFreq,
    emailLower,
    password,
  }) => {
    const weight = 1.0;
    const domain = "user";
    const components = [];

    //Initialize new user
    return axios.post(auth_API_URL + "signup", {
      fname,
      lname,
      assocSel,
      lab: lab || "",
      edu,
      exp,
      expEEG,
      expMoBI,
      expICA,
      cleanFreq,
      domain,
      weight,
      emailLower,
      password,
      components,
    });
  };

  // Forgot-password: ask the server to email a 6-digit reset code. The
  // reply is intentionally generic (the server doesn't reveal whether the
  // email exists), so callers show the same "check your inbox" message
  // regardless of the resolved value.
  forgotPassword = (email) => {
    return axios.post(auth_API_URL + "forgot-password", { email });
  };

  // Reset-password: send email + the 6-digit code + the chosen new password.
  resetPassword = ({ email, otp, newPassword }) => {
    return axios.post(auth_API_URL + "reset-password", {
      email,
      otp,
      newPassword,
    });
  };

  render() {
    return (
      <AuthContext.Provider
        value={{
          Email: this.state.Email,
          Domain: this.state.Domain,
          Weight: this.state.Weight,
          Auth: this.state.Auth,
          Info: this.state.Info,
          Name: this.state.Name,
          TutorialDone: this.state.TutorialDone,
          PracticePassed: this.state.PracticePassed,
          ProfileComplete: this.state.ProfileComplete,
          Lab: this.state.Lab,
          LabNoticeDismissed: this.state.LabNoticeDismissed,
          updateLab: this.updateLab,
          dismissLabNotice: this.dismissLabNotice,
          authHeader: this.authHeader,
          login: this.login,
          googleLogin: this.googleLogin,
          logout: this.logout,
          signup: this.signup,
          verifyEmail: this.verifyEmail,
          resendVerification: this.resendVerification,
          forgotPassword: this.forgotPassword,
          resetPassword: this.resetPassword,
          completeTutorial: this.completeTutorial,
          completeProfile: this.completeProfile,
          setPracticePassed: this.setPracticePassed,
        }}
      >
        {this.props.children}
      </AuthContext.Provider>
    );
  }
}

const AuthConsumer = AuthContext.Consumer;

export { AuthContext, AuthProvider, AuthConsumer, clearStoredSession };
